import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Meetings API
export const meetingsAPI = {
  getAll: async () => {
    const response = await api.get('/meetings');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/meetings/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/meetings', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/meetings/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/meetings/${id}`);
    return response.data;
  },
  uploadAudio: async (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/meetings/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },
  updateActionItem: async (meetingId, actionId, completed) => {
    const response = await api.put(`/meetings/${meetingId}/actions/${actionId}?completed=${completed}`);
    return response.data;
  },
  saveInterimTranscript: async (id, transcript) => {
    const response = await api.put(`/meetings/${id}/interim-transcript`, { transcript });
    return response.data;
  },
  analyzeChunk: async (id, audioBlob) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'chunk.webm');
    const response = await api.post(`/meetings/${id}/analyze-chunk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
  createDemo: async () => {
    const response = await api.post('/demo/create');
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  send: async (message, meetingId = null, sessionId = null) => {
    const response = await api.post('/chat', { message, meeting_id: meetingId, session_id: sessionId });
    return response.data;
  },
};

// Search API
export const searchAPI = {
  search: async (query, limit = 20) => {
    const response = await api.post('/search', { query, limit });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getConfig: async () => {
    const response = await api.get('/admin/config');
    return response.data;
  },
  updateConfig: async (updates) => {
    const response = await api.put('/admin/config', updates);
    return response.data;
  },
  listApiKeys: async () => (await api.get('/admin/api-keys')).data,
  createApiKey: async (data) => (await api.post('/admin/api-keys', data)).data,
  revokeApiKey: async (id) => (await api.delete(`/admin/api-keys/${id}`)).data,
};

export default api;
